"use client"

import { useState } from "react"
import SelectBox from "./SelectBox"
import { LineChart } from "lucide-react"
import Icon from "../utils/Icon"
import Loading from "../utils/Loading"

const NewReleaseTable = ({
  newReleaseData,
  onDashboardClick,
  selectedCompanies,
  onCompanyClick,
  addAllCompanies,
  addCompany,
  removeCompany,
  registeredCompanies,
  category,
  handleSummaryClick,
  handleAnalysisClick,
  isMobile,
  isFiltering,
}) => {
  const [showSelectAllTooltip, setShowSelectAllTooltip] = useState(false)

  const onSelectAllClick = () => {
    const companies = newReleaseData.map((company) => ({
      secCode: company.sec_code,
      companyName: company.company_name,
    }))
    addAllCompanies(companies)
  }

  const handleRegistCompany = (secCode, isRegistered) => {
    if (isRegistered) {
      removeCompany(secCode)
    } else {
      addCompany(secCode)
    }
  }

  const getCategory = (category) => {
    if (category == "timely-disclosure") {
      return "適時開示"
    } else if (category == "sec-report") {
      return "有価証券報告書"
    } else if (category == "press-release") {
      return "プレスリリース"
    } else if (category == "esg") {
      return "ESG"
    } else {
      return ""
    }
  }

  const toDate = (timestamp) => {
    if (!timestamp) return ""
    const date = new Date(timestamp)
    if (category === "timely-disclosure") {
      const minutes = date.getMinutes()
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:${minutes === 0 ? "00" : minutes}`
    } else {
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
    }
  }

  const onSummaryClick = (company, category) => {
    handleSummaryClick(company, category)
  }

  const onAnalysisClick = (company) => {
    handleAnalysisClick(company)
  }

  return (
    <div className="companies-table-container scrollbar-hide relative">
      <span id="earningsReportTitle"></span>
      {newReleaseData ? (
        <>
          {/* フィルタリング中のオーバーレイ */}
          {isFiltering && (
            <div className="absolute inset-0 bg-white bg-opacity-70 z-10 flex flex-col items-center justify-center">
              <div className="animate-spin w-8 h-8 border-4 border-gray-600 border-t-transparent rounded-full mb-2"></div>
              <p className="text-gray-700 font-medium">フィルタリング中...</p>
            </div>
          )}

          {/* PC用テーブル - スマホでは非表示 */}
          <div className={`table-margin ${isMobile ? "hidden" : "block"}`}>
            {showSelectAllTooltip && <div className="select-all-tooltip">すべて選択する</div>}
            <table id="earningsReportTable" className="companies-table w-full">
              <thead>
                <tr>
                  <th
                    className="th-graph-checkbox top-right-radius-5"
                    onClick={onSelectAllClick}
                    onMouseEnter={() => setShowSelectAllTooltip(true)}
                    onMouseLeave={() => setShowSelectAllTooltip(false)}
                  >
                    <LineChart />
                  </th>
                  <th className="th-company-name top-left-radius-5">企業名</th>
                  <th className="th-industsry  w-[150px]">業種</th>
                  <th className="th-date">日付</th>
                  <th className="th-title">タイトル</th>
                  {["press-release", "timely-disclosure"].includes(category) && (
                    <>
                      <th className="th-summary w-[100px]">要約</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {newReleaseData.map((company) => (
                  <tr key={company.doc_id}>
                    <td>
                      <SelectBox
                        secCode={company.sec_code}
                        companyName={company.company_name}
                        selectedCompanies={selectedCompanies}
                        onCompanyClick={onCompanyClick}
                      />
                    </td>
                    <td>
                      <div className="flex">
                        <div className="mr-2 flex items-center">
                          <div className="w-[25px] h-[25px] ">
                            <Icon secCode={company.sec_code} companyImageUrl={company.image} />
                          </div>
                        </div>
                        <div>
                          <div className="dashboard-link py-2 mb-2" onClick={() => onDashboardClick(company.sec_code)}>
                            {company.company_name}
                          </div>
                          <div className="flex">
                            <div className="ml-4">({company.sec_code})</div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="td-industry w-[150px]">{company.industry_name}</td>
                    <td>{toDate(company.date)}</td>
                    <td className="max-w-[300px]">
                      <a target="_blank" href={company.url} rel="noreferrer">
                        {company.title}
                      </a>
                    </td>
                    {["press-release", "timely-disclosure"].includes(category) && (
                      <>
                        <td className="th-summary !text-center">
                          <button
                            className="bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-full p-[5px] w-[45px] md:w-[50px] h-[30px]"
                            onClick={() => {
                              onSummaryClick(company, category)
                            }}
                          >
                            要約
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* スマホ用カードビュー - PCでは非表示 */}
          <div className={`${isMobile ? "block" : "hidden"} space-y-4 px-2 overflow-y-auto`}>
            {newReleaseData.map((company) => (
              <div key={company.doc_id} className="border rounded-lg p-4 bg-white shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-start gap-2">
                    <div className="w-[25px] h-[25px] mt-1">
                      <Icon secCode={company.sec_code} companyImageUrl={company.image} />
                    </div>
                    <div>
                      <div
                        className="font-medium text-sm dashboard-link"
                        onClick={() => onDashboardClick(company.sec_code)}
                      >
                        {company.company_name}
                      </div>
                      <div className="text-xs text-gray-500">({company.sec_code})</div>
                    </div>
                  </div>
                </div>

                <div className="text-sm mb-3">
                  <a target="_blank" href={company.url} className="text-gray-600 hover:underline" rel="noreferrer">
                    {company.title}
                  </a>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-500">{toDate(company.date)}</span>

                  <div className="flex gap-2">
                    {["press-release", "timely-disclosure"].includes(category) && (
                      <button
                        className="bg-gray-700 text-white rounded-full flex items-center justify-center hover:bg-gray-600 text-xs w-[45px] md:w-[50px] h-[30px]"
                        onClick={() => {
                          onSummaryClick(company, category)
                        }}
                      >
                        要約
                      </button>
                    )}
                    <SelectBox
                      secCode={company.sec_code}
                      companyName={company.company_name}
                      selectedCompanies={selectedCompanies}
                      onCompanyClick={onCompanyClick}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="flex justify-center items-center h-[100px]">
          <Loading />
        </div>
      )}
    </div>
  )
}

export default NewReleaseTable
